import { RELEASE_VERSION, ROUTES } from "@lhd/constants";
import {
  Dispatch,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdKeyboardArrowRight } from "react-icons/md";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { BsChatLeftDots } from "react-icons/bs";

import { useNavItem } from "./NavItems";
import { Styles, NameCompanySelected } from "./styled";
import { ArrowSidebar, Catalog } from "@lhd/assets";
import { user } from "@lhd/features/users/selector";
import { useSelector } from "react-redux";
import { getCompanyId } from "@lhd/utility";
import ButtonVerticalCollapse from "../ButtonVerticalCollapse";
type IProps = {
  isCollapse?: boolean;
  setIsCollapse: Dispatch<SetStateAction<boolean>>;
  setIsOpenByHover: Dispatch<SetStateAction<boolean>>;
  setIsSubBusinessNav: Dispatch<SetStateAction<boolean>>;
  setIsSubPersonalNav: Dispatch<SetStateAction<boolean>>;
  setIsSubOperationNav: Dispatch<SetStateAction<boolean>>;
  setIsSubAppStoreNav: Dispatch<SetStateAction<boolean>>;
  setIsSubAppraisalNav: Dispatch<SetStateAction<boolean>>;
  setIsSubManualNav: Dispatch<SetStateAction<boolean>>;
  setIsSubPermissionSalaryNav: Dispatch<SetStateAction<boolean>>;
  setIsSubOrganizationNav: Dispatch<SetStateAction<boolean>>;
  setIsSubSalaryNav: Dispatch<SetStateAction<boolean>>;
  setIsSubCompanyNav?: Dispatch<SetStateAction<boolean>>;
  divHeight: number;
  maintenanceBanner: {
    message: string;
    backgroundColor: string;
  } | null;
};
const SideBar = ({
  isCollapse,
  setIsCollapse,
  setIsOpenByHover,
  setIsSubBusinessNav,
  setIsSubPersonalNav,
  setIsSubOperationNav,
  setIsSubAppStoreNav,
  setIsSubManualNav,
  setIsSubAppraisalNav,
  setIsSubPermissionSalaryNav,
  setIsSubOrganizationNav,
  setIsSubSalaryNav,
  setIsSubCompanyNav,
  divHeight,
  maintenanceBanner,
}: IProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathName = location?.pathname?.split("/")[1];
  const fullPathName = location?.pathname;
  const auth = useSelector(user);
  const companyId = getCompanyId();
  const nameCompanySelectedRef = useRef<HTMLDivElement>(null);
  const [isOverNameCompany, setIsOverNameCompany] = useState(false);

  const nameCompany = useMemo(() => {
    return (
      auth?.companies.find((company) => company.id.toString() == companyId)
        ?.name ||
      auth?.companies.find((company) => company.is_work_default)?.name
    );
  }, [auth, companyId]);

  function extractCompanyName(str: string) {
    // Case with 株式会社 or variants with flexible spacing
    const regex = /^\s*(?:株\s*)?式\s*会\s*社\s*(.+)$/;

    // Check and replace if matched
    const match = str.match(regex);
    return match ? match[1].trim() : str.trim(); // Return company name or keep original if not matched
  }

  const formatCompanyName = (name: string | undefined): string => {
    if (!name) return "";
    const maxCharsPerLine = 15;
    const maxLines = 2;
    const maxTotalChars = maxCharsPerLine * maxLines; // 30 characters

    // If <= 15 characters, display 1 line
    if (name.length <= maxCharsPerLine) {
      return name;
    }

    // If > 15 characters, display max 2 lines
    const firstLine = name.substring(0, maxCharsPerLine);

    if (name.length <= maxTotalChars) {
      // If <= 30 characters, display 2 full lines
      const secondLine = name.substring(maxCharsPerLine);
      return `${firstLine}\n${secondLine}`;
    } else {
      // If > 30 characters, display 2 lines and add "..."
      const secondLine = name.substring(maxCharsPerLine, maxTotalChars);
      return `${firstLine}\n${secondLine}...`;
    }
  };

  const extractedName = useMemo(() => {
    return extractCompanyName(nameCompany || "");
  }, [nameCompany]);

  const isTwoLines = useMemo(() => {
    return extractedName.length > 15;
  }, [extractedName]);

  const { navItems } = useNavItem();
  const subMeneSalaryPatch = [
    ROUTES.ANNUAL_SALARY_LIST,
    ROUTES.ANNUAL_SALARY_SUMMARIES,
    ROUTES.SALARY_CONFIGURATION,
    ROUTES.ANNUAL_PAYMENT_LIST,
    ROUTES.ANNUAL_PAYMENT_SUMMARIES,
  ];

  const subMenuBusinessPatch = [
    ROUTES.CUSTOMER_LIST,
    ROUTES.CUSTOMER_HISTORY_SUMMARIES,
    ROUTES.PROJECT_MANAGE,
    // ROUTES.APPS,
    ROUTES.SETTING_PRIVILEGE,
  ];
  const subMenuOrganizationPatch = [
    ROUTES.ORGANIZATION_CHART,
    ROUTES.WORKERS,
    ROUTES.USERS,
  ];

  const subMenuPersonalPatch = [
    ROUTES.BRANCH,
    ROUTES.ORGANIZATIONS,
    ROUTES.DEPARTMENTS,
    ROUTES.ORGS,
    ROUTES.DIVISIONS,
    ROUTES.BRANCH_CHART,
    // ROUTES.PAYSLIP,
    ROUTES.AUTH,
    ROUTES.MANAGEMENT,
    // ROUTES.ORGANIZATION_MANAGEMENT,
    ROUTES.POSITIONS,
  ];
  const subMenuOperationPatch = [
    ROUTES.COMPANIES,
    ROUTES.ADMIN_CONFIG,
    ROUTES.APP_CATEGORIES,
    ROUTES.APP_SUBMITTED_MANAGEMENT,
  ];
  const subMenuAppraisalPatch = [
    ROUTES.APPRAISAL_LIST,
    ROUTES.APPRAISAL_SHEET_LIST,
    ROUTES.APPRAISAL_ANALYST,
    ROUTES.APPRAISAL_KGI_LIST,
    ROUTES.APPRAISAL_SETTING,
    ROUTES.PERFORMANCE_APPRAISAL,
  ];
  const subAppStorePatch = [
    ROUTES.APP_CATEGORIES,
    ROUTES.APP_SUBMITTED_MANAGEMENT,
  ];
  const subPermissionsSalaryPatch = [
    ROUTES.PERMISSIONS_MANAGEMENT,
    ROUTES.ORGANIZATION_MANAGEMENT,
    // ROUTES.COMPANY_CONFIGURATION,
  ];

  const handleOnClick = (index: number) => {
    const item = navItems[index] as any;
    if (item) {
      setIsSubSalaryNav(!!item.subSalaryItems);
      setIsSubBusinessNav(!!item.subBusinessItems);
      setIsSubPersonalNav(!!item.subPersonalItems);
      setIsSubOperationNav(!!item.subOperationItems);
      setIsSubAppStoreNav(!!item.subAppStoreItems);
      setIsSubAppraisalNav(!!item.subAppraisalItems);
      setIsSubManualNav(!!item.subManualItems);
      setIsSubPermissionSalaryNav(!!item.subPermissionSalaryItems);
      setIsSubOrganizationNav(!!item.subOrganizationItems);
      if (setIsSubCompanyNav) {
        setIsSubCompanyNav(!!item.subCompanyItems);
      }
    }
    setIsCollapse(false);
  };

  useEffect(() => {
    if (nameCompanySelectedRef.current) {
      const isOver =
        nameCompanySelectedRef.current.offsetWidth <
        nameCompanySelectedRef.current.scrollWidth;
      setIsOverNameCompany(isOver);
    }
  }, [nameCompanySelectedRef.current, nameCompany]);

  const heightMaintenanceBanner =
    document.getElementById("maintenance-banner")?.offsetHeight;

  return (
    <Styles
      className="side-bar collapse"
      isCollapse={isCollapse}
      divHeight={divHeight}
      style={{
        top:
          (isCollapse ? divHeight + 48 : 48) +
          (maintenanceBanner ? heightMaintenanceBanner || 0 : 0),
      }}
    >
      {!isCollapse && (
        <NameCompanySelected
          ref={nameCompanySelectedRef}
          $isTwoLines={isTwoLines}
          $isOverNameCompany={isOverNameCompany}
        >
          {formatCompanyName(extractedName)}
        </NameCompanySelected>
      )}
      <div className="collapse-box">
        <ButtonVerticalCollapse
          directionArrow="right"
          onClick={() => setIsCollapse(false)}
          directionRadius="right"
          variant="danger"
          style={{ minHeight: 52 }}
        >
          開
        </ButtonVerticalCollapse>
      </div>
      {/* Hide all menu items when collapsed - only show collapse icon */}
      <ul style={{ display: "none" }}>
        {navItems.map((item: any, index) => {
          const isActive =
            (pathName === ROUTES.APPS && item?.subAppItems) ||
            (subMeneSalaryPatch.includes(pathName) && item?.subSalaryItems) ||
            (subPermissionsSalaryPatch.includes(pathName) &&
              item?.subPermissionSalaryItems) ||
            // (subMenuBusinessPatch.includes(pathName) &&
            //   item?.subBusinessItems) ||
            (subMenuOrganizationPatch.includes(pathName) &&
              item?.subOrganizationItems) ||
            (subMenuPersonalPatch.includes(pathName) &&
              item?.subPersonalItems) ||
            (subMenuOperationPatch.includes(pathName) &&
              item?.subOperationItems) ||
            (subAppStorePatch.includes(pathName) && item?.subAppStoreItems) ||
            (subMenuAppraisalPatch.includes(pathName) &&
              item?.subAppraisalItems) ||
            (subMenuOperationPatch.includes(fullPathName) &&
              item?.subOperationItems) ||
            (pathName === "financial-report" &&
              item?.navigate === "financial-report") ||
            (pathName === ROUTES.PROJECT_MANAGE &&
              item?.navigate === ROUTES.PROJECT_MANAGE) ||
            ((pathName === ROUTES.CUSTOMER_LIST ||
              pathName === ROUTES.CUSTOMER_HISTORY_SUMMARIES ||
              fullPathName?.startsWith(`/${ROUTES.CUSTOMER_LIST}`)) &&
              item?.navigate === ROUTES.CUSTOMER_HISTORY_SUMMARIES) ||
            (pathName === ROUTES.Electronic_Learning &&
              item?.navigate === ROUTES.Electronic_Learning) ||
            (pathName === ROUTES.CORRELATION_DIAGRAM &&
              item?.navigate === ROUTES.CORRELATION_DIAGRAM) ||
            (pathName === "tracking_time" &&
              item?.navigate === ROUTES.TRACKING_TIME) ||
            (pathName === ROUTES.ANNUAL_ORG_SUMMARIES &&
              item?.navigate === ROUTES.ANNUAL_ORG_SUMMARIES) ||
            (item?.id === 2 &&
              (pathName === ROUTES.WORKERS ||
                pathName === ROUTES.PERSONNEL ||
                pathName === ROUTES.ORGANIZATION_CHART ||
                fullPathName?.startsWith(`/${ROUTES.WORKERS}`))) ||
            (pathName === ROUTES.LIST_MESS &&
              item?.navigate === ROUTES.LIST_MESS);
          return (
            <li
              key={index}
              onClick={() => handleOnClick(index)}
              className={"nav-link" + ` ${isActive ? "active" : ""}`}
            >
              {item?.icon || (item?.iconActive && item?.iconNotActive) ? (
                <div>
                  {item?.icon ||
                    (isActive ? item?.iconActive : item?.iconNotActive)}
                </div>
              ) : (
                <div
                  style={{
                    margin: 2,
                  }}
                >
                  <Catalog stroke="#666666" width={20} height={20} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {/* Customer inquiry button hidden when sidebar is collapsed */}
      {/* <div
        className="customer-inquiry-button collapse"
        onClick={() => navigate(`/${ROUTES.LIST_MESS}`)}
      >
        <BsChatLeftDots
          size={20}
          style={{
            color: "#ffffff",
            flexShrink: 0,
          }}
        />
      </div> */}
      <div className={"wrap-footer wrap-footer-collapse"}>
        <span>{RELEASE_VERSION}</span>
      </div>
    </Styles>
  );
};

export default memo(SideBar);
